"use client";

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import { Sheet, SheetFooter } from "@/components/ui/sheet";
import { Button, SubmitButton } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { showToast } from "@/components/ui/sonner";
import { customInstance } from "@/lib/api/client";
import { TalentSearchFiltersProvider } from "@/components/search/talent-search-filters-provider";
import type { SearchFilters } from "@/lib/api/types";
import { FOCUS_RING } from "../icon-button";
import { PresetSheetHeader } from "../preset-sheet-header";
import { PresetFormFields } from "../preset-form-fields";
import {
  buildDefaultValues,
  presetFormSchema,
  serializePresetEntities,
  type EntityItem,
  type PresetFormValues,
  type PresetInitialData,
  type Visibility,
} from "../schema";
import { getPresetMeta, type PresetTypeSlug } from "../../lib/categories";
import { serializeStagePresetItems } from "../../lib/stage-preset";

const EMPTY_FILTERS: SearchFilters = {};

export interface ManagePresetProps {
  open: boolean;
  onClose: () => void;
  presetType?: PresetTypeSlug;
  presetId: string | undefined;
  initialData?: PresetInitialData;
  /** When provided, defer destructive deletion to the parent (e.g. shared confirmation modal). */
  onRequestDelete?: () => void;
  /** True while the parent is performing a confirmed delete; disables the Delete button. */
  deletePending?: boolean;
}

export function ManagePreset({
  open,
  onClose,
  presetType: initialPresetType,
  presetId,
  initialData,
  onRequestDelete,
  deletePending = false,
}: ManagePresetProps) {
  const queryClient = useQueryClient();

  const form = useForm<PresetFormValues>({
    resolver: zodResolver(presetFormSchema),
    mode: "onChange",
    defaultValues: buildDefaultValues(
      initialPresetType ?? "companies",
      initialData,
    ),
  });

  const presetType = useWatch({ control: form.control, name: "presetType" });
  const isFilterType = presetType === "filters";
  const editSyncedPresetIdRef = useRef<string | undefined>(undefined);
  const [draftState, setDraftState] = useState<{
    resetKey: string;
    filters: SearchFilters;
  } | null>(null);
  const [draftOverride, setDraftOverride] = useState<{
    presetId: string | undefined;
    version: number;
    filters: SearchFilters;
  } | null>(null);

  useEffect(() => {
    if (!open) {
      editSyncedPresetIdRef.current = undefined;
      return;
    }
    if (!presetId || !initialData) return;
    if (editSyncedPresetIdRef.current === presetId) return;
    editSyncedPresetIdRef.current = presetId;
    form.reset(
      buildDefaultValues(initialPresetType ?? "companies", initialData),
    );
  }, [open, presetId, initialData, initialPresetType, form]);

  const activeDraftOverride =
    draftOverride?.presetId === presetId ? draftOverride : null;
  const draftSeedFilters =
    activeDraftOverride?.filters ?? initialData?.filters ?? EMPTY_FILTERS;
  const draftResetKey = `${presetId ?? "new"}:${initialData ? "ready" : "pending"}:${activeDraftOverride?.version ?? 0}`;

  const handleDraftFiltersChange = useCallback(
    (nextFilters: SearchFilters) => {
      setDraftState({
        resetKey: draftResetKey,
        filters: nextFilters,
      });
    },
    [draftResetKey],
  );

  const handleClose = useCallback(() => {
    setDraftState(null);
    setDraftOverride(null);
    onClose();
  }, [onClose]);

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      customInstance(`/api/presets/${presetId}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["presets"] });
      showToast.success("Preset updated");
      handleClose();
    },
    onError: () => {
      showToast.error("Failed to update preset");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () =>
      customInstance(`/api/presets/${presetId}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["presets"] });
      showToast.success("Preset deleted");
      handleClose();
    },
    onError: () => {
      showToast.error("Failed to delete preset");
    },
  });

  const handleDeleteClick = useCallback(() => {
    if (onRequestDelete) onRequestDelete();
    else deleteMutation.mutate();
  }, [onRequestDelete, deleteMutation]);

  const handleReset = useCallback(() => {
    form.reset(buildDefaultValues(presetType));
    if (isFilterType) {
      setDraftState(null);
      setDraftOverride((current) => ({
        presetId,
        version: (current?.version ?? 0) + 1,
        filters: EMPTY_FILTERS,
      }));
    }
  }, [form, presetType, isFilterType, presetId]);

  const onValidSubmit = useCallback(
    (values: PresetFormValues) => {
      const typeMeta = getPresetMeta(values.presetType);
      const payload: Record<string, unknown> = {
        preset_type: typeMeta.apiType,
        title: values.title,
        description: values.description || undefined,
        visibility: values.visibility,
        items:
          values.presetType === "filters" || values.presetType === "job-stages"
            ? []
            : serializePresetEntities(values.entities),
      };

      if (values.presetType === "filters") {
        const filters =
          draftState?.resetKey === draftResetKey
            ? draftState.filters
            : draftSeedFilters;
        payload.filters_json = JSON.stringify(filters);
      }

      if (values.presetType === "job-stages") {
        payload.stages_json = JSON.stringify(
          serializeStagePresetItems(values.stages),
        );
      }

      updateMutation.mutate(payload);
    },
    [draftResetKey, draftSeedFilters, draftState, updateMutation],
  );

  const isPending = updateMutation.isPending;
  const canSubmit = form.formState.isValid;
  const submitHelpId = useId();

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleClose();
      }}
      size="md"
      disableFocusTrap
    >
      <PresetSheetHeader title="Edit Preset" />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onValidSubmit)}
          className="flex min-h-0 flex-1 flex-col"
        >
          <div className="flex min-h-0 flex-1 flex-col gap-5 bg-background dark:bg-surface-elevated px-4 py-4 sm:px-6 sm:py-6 overflow-y-auto">
            {isFilterType ? (
              <TalentSearchFiltersProvider
                initialFilters={draftSeedFilters}
                onFiltersChange={handleDraftFiltersChange}
                resetKey={draftResetKey}
              >
                <PresetFormFields form={form} isEdit />
              </TalentSearchFiltersProvider>
            ) : (
              <PresetFormFields form={form} isEdit />
            )}
          </div>

          <SheetFooter className="justify-between">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                aria-label="Delete preset"
                onClick={handleDeleteClick}
                disabled={deletePending || deleteMutation.isPending}
                icon={
                  <Icon
                    name="IconTrashCanSimple"
                    size={16}
                    aria-hidden="true"
                    className="text-heading opacity-60"
                  />
                }
                className={`relative before:absolute before:left-1/2 before:top-1/2 before:h-11 before:w-full before:min-w-11 before:-translate-x-1/2 before:-translate-y-1/2 before:content-[''] ${FOCUS_RING}`}
              >
                <span className="px-1">Delete</span>
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                aria-label="Clear all preset fields"
                onClick={handleReset}
                icon={
                  <Icon
                    name="IconBroomSparkle"
                    size={16}
                    aria-hidden="true"
                    className="text-heading opacity-60"
                  />
                }
                className={`relative before:absolute before:left-1/2 before:top-1/2 before:h-11 before:w-full before:min-w-11 before:-translate-x-1/2 before:-translate-y-1/2 before:content-[''] ${FOCUS_RING}`}
              >
                <span className="px-1">Clear</span>
              </Button>
            </div>
            <div className="flex flex-col items-end gap-1">
              <SubmitButton
                type="submit"
                variant="primary"
                size="sm"
                disabled={!canSubmit}
                loading={isPending}
                loadingLabel="Saving"
                aria-describedby={!canSubmit ? submitHelpId : undefined}
              >
                Save
              </SubmitButton>
            </div>
          </SheetFooter>
        </form>
      </Form>
    </Sheet>
  );
}

export type { EntityItem, Visibility, PresetInitialData };
